import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../shared/Icon';
import { useEscapeKey } from '../shared/useEscapeKey';
import { generatedKpiLabel, kpiCardLabel } from './renderers/kpiFormat';
import './WidgetCreatorModal.css';

// PRD-1578: "Edit" on a widget changes its words and nothing else. The full wizard
// re-runs the prompt before it will save, which can come back with different SQL and a
// different number for an edit that only renamed the card -- that path is "Edit prompt"
// now. This saves through the narrow text PATCH: no query, and the result on the card
// is left exactly as it is.
//
// A KPI is named by the label under its value (it shows no title), so that is the field
// here; every other type keeps its title. A KPI's field opens on whatever the card shows
// right now: the author's label, or the generated unit + subtitle when never edited.

// Gates the whole PRD-1578 presentation change: this dialog and the Edit / Edit prompt
// split, the KPI Label step, and the KPI card's label-instead-of-title. Off means the
// card, menu and wizard behave as before. ID columns without separators are not gated.
export const WIDGET_TEXT_EDIT_FLAG = 'enable_widget_text_edit';

const isKpi = (widget) => widget?.widget_type_id === 'kpi';

export function initialText(widget) {
  if (!widget) return '';
  return isKpi(widget) ? kpiCardLabel(widget.data, widget.display_label) : (widget.title || '');
}

// What the PATCH carries. A cleared KPI label goes back to the generated text, so it is
// sent as null rather than refused; a title cannot be blank. A KPI's title is hidden on
// its card but still names it in the delete dialog, sharing and drill-down, so it
// follows a non-empty label -- otherwise "Delete" would name a widget nobody can see.
//
// Clearing a label also resets the title, to the generated text (or the prompt), so it
// stops naming the widget by a label the card no longer shows (review on #249).
export function textPatchFor(widget, value) {
  const text = String(value ?? '').trim();
  if (!isKpi(widget)) return { title: text };
  if (text) return { display_label: text, title: text };
  const fallbackTitle = generatedKpiLabel(widget.data) || String(widget.prompt || '').trim().slice(0, 40);
  return fallbackTitle ? { display_label: null, title: fallbackTitle } : { display_label: null };
}

// Mounted once per widget (the parent keys it on the widget id), so the field starts
// with its text on the first frame. Filling it from an effect drew one empty frame
// first, which the e2e run caught as an empty field.
function WidgetTextEditModal({ widget, onClose, onSave }) {
  const [value, setValue] = useState(() => initialText(widget));
  const [saving, setSaving] = useState(false);
  // While a save is in flight the dialog cannot be dismissed: if it fails, the typed
  // text has to still be here (review on #249).
  useEscapeKey(Boolean(widget) && !saving, onClose);

  if (!widget) return null;

  const kpi = isKpi(widget);
  const blankTitle = !kpi && !value.trim();
  const unchanged = value.trim() === initialText(widget).trim();

  const handleSave = async () => {
    if (blankTitle || saving) return;
    if (unchanged) {
      onClose();
      return;
    }
    setSaving(true);
    // The parent reports a failure and keeps the dialog open, so the edit is not lost.
    const ok = await onSave(widget, textPatchFor(widget, value));
    setSaving(false);
    if (ok) onClose();
  };

  return createPortal(
    <div className="wcm-overlay">
      <div className="wcm-modal wcm-modal--compact" role="dialog" aria-modal="true" aria-labelledby="wte-title">
        <div className="wcm-header">
          <h2 className="wcm-title" id="wte-title">Edit widget</h2>
          <button className="wcm-close" onClick={onClose} aria-label="Close" disabled={saving}>
            <Icon name="close" />
          </button>
        </div>
        <div className="wcm-divider" />
        <div className="wcm-body">
          <div className="wcm-field">
            <label className="wcm-label" htmlFor="wte-input">{kpi ? 'Label' : 'Widget Title'}</label>
            <input
              id="wte-input"
              className="resui-input"
              type="text"
              maxLength={200}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              // Not while an IME is composing: Enter there confirms a character, not the form.
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleSave(); }}
              autoFocus
            />
          </div>
          <p className="wcm-hint">
            {kpi
              ? 'Shown under the value. Leave it empty to use the generated label.'
              : 'Changing the title does not re-run the widget. Use Edit prompt to change what it asks.'}
          </p>
        </div>
        <div className="wcm-divider" />
        <div className="wcm-footer">
          <button className="wcm-back-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="wcm-next-btn" onClick={handleSave} disabled={blankTitle || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default WidgetTextEditModal;
